import { DataGridProProps } from '@mui/x-data-grid-pro';
import { Permission } from '@procureai/permissions';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DiscountCell } from '../../cells/DiscountCell/DiscountCell';
import { HeaderCell } from '../../cells/HeaderCell/HeaderCell';
import { ItemSelectionCell } from '../../cells/ItemSelectionCell/ItemSelectionCell';
import { LabelCell } from '../../cells/LabelCell/LabelCell';
import { ProcessedByCell } from '../../cells/ProcessedByCell/ProcessedByCell';
import { QuantityWithUnitCell } from '../../cells/QuantityWithUnitCell/QuantityWithUnitCell';
import { QuoteItemCell } from '../../cells/QuoteItemCell/QuoteItemCell';
import { SourceReferenceCell } from '../../cells/SourceRefenceCell/SourceReferenceCell';
import { SourcingCaseStatusChipCell } from '../../cells/SourcingCaseStatusChipCell/SourcingCaseStatusChipCell';
import {
  ContractDemandSourcingCase,
  ContractDemandSourcingCaseQuoteItem,
} from '../../ContractDemandAwardingWidget.types';
import { TsrctSourcingCaseQuoteItemStatus, TsrctStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { CurrencyCell, DateRangeCell } from '~/components/DataGridCustom';
import { CommodityLinkWithDescriptionCell } from '~/components/DataGridCustom/cells/CommodityLinkWithDescriptionCell/CommodityLinkWithDescriptionCell';
import { RouteId } from '~/routes';

import { AwardingDataGridRow, AwardingDataGridRowType, UseAwardingDataGridConfig } from './AwardingDataGrid.types';

const COL_MIN_WIDTH = 250;
const COL_MAX_WIDTH = 400;

export const CROSSED_OUT_CLASSNAME = 'crossed-out';

const QUOTE_ITEM_STATE_TO_CLASS_NAME: Record<TsrctSourcingCaseQuoteItemStatus, string> = {
  [TsrctSourcingCaseQuoteItemStatus.NOT_REQUESTED]: CROSSED_OUT_CLASSNAME,
  [TsrctSourcingCaseQuoteItemStatus.REQUESTED]: CROSSED_OUT_CLASSNAME,
  [TsrctSourcingCaseQuoteItemStatus.BID_PROVIDED]: '',
  [TsrctSourcingCaseQuoteItemStatus.BID_DECLINED]: CROSSED_OUT_CLASSNAME,
  [TsrctSourcingCaseQuoteItemStatus.AGREEMENT_REACHED]: '',
  [TsrctSourcingCaseQuoteItemStatus.NEGOTIATION_FAILED]: CROSSED_OUT_CLASSNAME,
  [TsrctSourcingCaseQuoteItemStatus.AWARDED]: '',
  [TsrctSourcingCaseQuoteItemStatus.OFFER_DISMISSED]: '',
  [TsrctSourcingCaseQuoteItemStatus.FINAL_OFFER_PROPOSED]: '',
};

export const useAwardingDataGridConfig: UseAwardingDataGridConfig<AwardingDataGridRow> = ({
  contractDemand,
  lineItemAssignments,
}) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDemandAwardingWidget' });

  return useMemo(() => {
    const sourcingCases = contractDemand.sourcingCases ?? [];
    const demandRequest = contractDemand.demandRequest!;
    const demandRequestLineItems = contractDemand.demandRequest?.lineItems ?? [];

    // Build a lookup table to quickly find quote items by sourcing case ID and demand request item ID
    // during row rendering of the data grid.
    const quoteItemsBySourcingCaseAndRequestItemId: Record<
      string,
      Record<string, ContractDemandSourcingCaseQuoteItem>
    > = sourcingCases.reduce(
      (acc, sourcingCase) => {
        const itemsByDemandRequestItemId: Record<string, ContractDemandSourcingCaseQuoteItem> = {};
        sourcingCase.quote?.lineItems?.forEach((item) => {
          if (item.demandRequestItemId) {
            itemsByDemandRequestItemId[item.demandRequestItemId] = item;
          }
        });
        acc[sourcingCase.id] = itemsByDemandRequestItemId;
        return acc;
      },
      {} as Record<string, Record<string, ContractDemandSourcingCaseQuoteItem>>,
    );

    const columns: DataGridProProps<AwardingDataGridRow>['columns'] = [
      {
        display: 'flex',
        field: 'label',
        minWidth: COL_MIN_WIDTH,
        maxWidth: COL_MAX_WIDTH,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => {
          return row.renderLabelCell ? row.renderLabelCell() : null;
        },

        renderHeader: () => null,
      },
      ...sourcingCases.map((sourcingCase, index): DataGridProProps<AwardingDataGridRow>['columns'][number] => ({
        display: 'flex',
        field: `sourcingCase-${index}`,
        minWidth: COL_MIN_WIDTH,
        maxWidth: COL_MAX_WIDTH,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => {
          return row.renderContentCell ? row.renderContentCell(sourcingCase) : null;
        },
        cellClassName: ({ row }) => {
          return row.getContentCellClassName ? row.getContentCellClassName(sourcingCase) : '';
        },
        renderHeader: () => <HeaderCell title={sourcingCase.supplier?.name ?? ''} />,
      })),
    ];

    const rows: AwardingDataGridRow[] = [
      {
        type: AwardingDataGridRowType.STATUS_HEADLINE,
        renderLabelCell: () => <LabelCell sectionHeader value={AwardingDataGridRowType.STATUS_HEADLINE} />,
      },
      {
        type: AwardingDataGridRowType.PROCESSED_BY,
        renderLabelCell: () => <LabelCell value={AwardingDataGridRowType.PROCESSED_BY} />,
        renderContentCell: (sourcingCase: ContractDemandSourcingCase) => (
          <ProcessedByCell
            value={{
              caseId: sourcingCase.id,
              caseSequenceNumber: sourcingCase.sequenceNumber,
            }}
          />
        ),
      },
      {
        type: AwardingDataGridRowType.STATUS,
        renderLabelCell: () => <LabelCell value={AwardingDataGridRowType.STATUS} />,
        renderContentCell: (sourcingCase: ContractDemandSourcingCase) => (
          <SourcingCaseStatusChipCell status={sourcingCase.status} />
        ),
      },
      {
        type: AwardingDataGridRowType.CONTRACTS,
        renderLabelCell: () => <LabelCell value={AwardingDataGridRowType.CONTRACTS} />,
        renderContentCell: (sourcingCase: ContractDemandSourcingCase) => (
          <SourceReferenceCell values={sourcingCase.sourceReferences} />
        ),
      },
      // --------------------------- Contract details ---------------------------
      {
        type: AwardingDataGridRowType.CONTRACT_HEADLINE,
        renderLabelCell: () => <LabelCell sectionHeader value={AwardingDataGridRowType.CONTRACT_HEADLINE} />,
      },
      {
        type: AwardingDataGridRowType.VALIDITY_PERIOD,
        renderLabelCell: () => <LabelCell value={AwardingDataGridRowType.VALIDITY_PERIOD} />,
        renderContentCell: (sourcingCase: ContractDemandSourcingCase) => (
          <DateRangeCell
            value={
              sourcingCase.quote
                ? {
                    from: sourcingCase.quote.validFrom,
                    to: sourcingCase.quote.validTo,
                  }
                : undefined
            }
          />
        ),
      },
      {
        type: AwardingDataGridRowType.INITIAL_TOTAL,
        renderLabelCell: () => <LabelCell value={AwardingDataGridRowType.INITIAL_TOTAL} />,
        renderContentCell: (sourcingCase: ContractDemandSourcingCase) => (
          <CurrencyCell
            value={sourcingCase.quote?.totalValue}
            currency={sourcingCase.quote?.currency}
            status={TsrctStatus.NEUTRAL}
          />
        ),
      },
      {
        type: AwardingDataGridRowType.DISCOUNT,
        renderLabelCell: () => <LabelCell value={AwardingDataGridRowType.DISCOUNT} />,
        renderContentCell: (sourcingCase: ContractDemandSourcingCase) => (
          <DiscountCell
            value={sourcingCase.quote?.totalPriceDiscount}
            percent={sourcingCase.quote?.discount}
            currency={sourcingCase.quote?.currency}
          />
        ),
      },
      {
        type: AwardingDataGridRowType.FINAL_TOTAL,
        renderLabelCell: () => <LabelCell value={AwardingDataGridRowType.FINAL_TOTAL} />,
        renderContentCell: (sourcingCase: ContractDemandSourcingCase) => (
          <CurrencyCell
            value={sourcingCase.quote?.totalDiscountedValue}
            currency={sourcingCase.quote?.currency ?? undefined}
          />
        ),
      },
      // --------------------------- Awarding details ---------------------------
      {
        type: AwardingDataGridRowType.AWARDING_HEADLINE,
        renderLabelCell: () => <LabelCell sectionHeader value={AwardingDataGridRowType.AWARDING_HEADLINE} />,
      },
      ...(lineItemAssignments.hasAssignableQuoteItems
        ? [
            {
              type: AwardingDataGridRowType.AWARD_ALL_ITEMS,
              renderContentCell: (sourcingCase: ContractDemandSourcingCase) => {
                if (
                  !lineItemAssignments.isAwardingActiveForSourcingCase(sourcingCase) ||
                  !lineItemAssignments.hasAssignableQuoteItemsForSourcingCase(sourcingCase)
                ) {
                  return null;
                }

                return (
                  <ItemSelectionCell
                    checked={lineItemAssignments.areAllLineItemsAssignedToSourcingCase(sourcingCase)}
                    onToggle={() => {
                      lineItemAssignments.assignAllLineItemsToSourcingCase(sourcingCase);
                    }}
                    label={t(`cell.itemSelection.awardAllItems`)}
                    bold
                  />
                );
              },
            },
          ]
        : []),
      ...demandRequestLineItems
        .map((demandRequestItem) => {
          return [
            {
              type: AwardingDataGridRowType.LINE_ITEM,
              renderLabelCell: () => (
                <CommodityLinkWithDescriptionCell
                  description={demandRequestItem.title ?? undefined}
                  value={demandRequestItem.commodityId}
                  to={RouteId.commodityDetail(demandRequestItem.commodityId)}
                  needs={{
                    permission: Permission.BaseCommodityList,
                  }}
                />
              ),
              renderContentCell: (sourcingCase: ContractDemandSourcingCase) => (
                <QuoteItemCell
                  demandRequestItem={demandRequestItem}
                  sourcingCase={sourcingCase}
                  lineItemAssignments={lineItemAssignments}
                  quoteItem={quoteItemsBySourcingCaseAndRequestItemId[sourcingCase.id][demandRequestItem.id]}
                />
              ),
            },
            {
              type: AwardingDataGridRowType.QUANTITY,
              renderLabelCell: () => (
                <LabelCell
                  value={AwardingDataGridRowType.QUANTITY}
                  additionalInfo={
                    <QuantityWithUnitCell value={demandRequestItem.quantity} unitId={demandRequestItem.unitId} />
                  }
                />
              ),
              renderContentCell: (sourcingCase: ContractDemandSourcingCase) => (
                <QuantityWithUnitCell
                  value={quoteItemsBySourcingCaseAndRequestItemId[sourcingCase.id][demandRequestItem.id].quantity}
                  unitId={demandRequestItem.unitId}
                />
              ),
              getContentCellClassName: (sourcingCase: ContractDemandSourcingCase) =>
                QUOTE_ITEM_STATE_TO_CLASS_NAME[
                  quoteItemsBySourcingCaseAndRequestItemId[sourcingCase.id][demandRequestItem.id].status
                ],
            },
            {
              type: AwardingDataGridRowType.PRICE_PER_PRICE_UNIT,
              renderLabelCell: () => (
                <LabelCell
                  value={AwardingDataGridRowType.PRICE_PER_PRICE_UNIT}
                  additionalInfo={
                    <CurrencyCell
                      value={demandRequestItem.baselinePricePerPriceUnit}
                      currency={demandRequest.currency}
                    />
                  }
                />
              ),
              renderContentCell: (sourcingCase: ContractDemandSourcingCase) => (
                <CurrencyCell
                  value={
                    quoteItemsBySourcingCaseAndRequestItemId[sourcingCase.id][demandRequestItem.id]
                      .discountedPricePerPriceUnit
                  }
                  valueStrikeThrough={
                    quoteItemsBySourcingCaseAndRequestItemId[sourcingCase.id][demandRequestItem.id].pricePerPriceUnit
                  }
                  currency={sourcingCase.quote?.currency}
                />
              ),
              getContentCellClassName: (sourcingCase: ContractDemandSourcingCase) =>
                QUOTE_ITEM_STATE_TO_CLASS_NAME[
                  quoteItemsBySourcingCaseAndRequestItemId[sourcingCase.id][demandRequestItem.id].status
                ],
            },
            {
              type: AwardingDataGridRowType.TOTAL_VALUE,
              renderLabelCell: () => (
                <LabelCell
                  value={AwardingDataGridRowType.TOTAL_VALUE}
                  additionalInfo={
                    <CurrencyCell value={demandRequestItem.totalValue} currency={demandRequest.currency} />
                  }
                />
              ),
              renderContentCell: (sourcingCase: ContractDemandSourcingCase) => (
                <CurrencyCell
                  value={
                    quoteItemsBySourcingCaseAndRequestItemId[sourcingCase.id][demandRequestItem.id].totalDiscountedValue
                  }
                  valueStrikeThrough={
                    quoteItemsBySourcingCaseAndRequestItemId[sourcingCase.id][demandRequestItem.id].totalValue
                  }
                  currency={sourcingCase.quote?.currency}
                />
              ),
              getContentCellClassName: (sourcingCase: ContractDemandSourcingCase) =>
                QUOTE_ITEM_STATE_TO_CLASS_NAME[
                  quoteItemsBySourcingCaseAndRequestItemId[sourcingCase.id][demandRequestItem.id].status
                ],
            },
          ];
        })
        .flat(),
    ].map((item, index) => ({ id: `${index}`, ...item }));

    return {
      columnHeaderHeight: 70,
      columns,
      rows,
      isRowSelectable: () => {
        return false;
      },
    };
  }, [contractDemand, t, lineItemAssignments]);
};
